// Reglas de la ficha del SKU de Inventario v3 (mock «Portal Admin VL v3»): lógica pura, sin React.
import { ApiError } from '@/api/errors';
import { skuSoldCents } from '@/api/services/retail-wizard/soldAmount';
import type {
    InventoryLensCoverage,
    InventoryLensPeriod,
    InventoryMovementEntry,
    InventoryMovementReason,
    InventoryMovementType,
    InventoryPieceEntry,
    InventoryPieceStatus,
    InventoryStorePieces,
    InventoryStoreReserved,
    SkuPriceHistory,
} from '@/api/services/retail-catalog/backoffice';
import type { BackofficeSale, BackofficeSalesFilters } from '@/api/services/retail-wizard/contract';
import { mxDay } from '@/lib/dateHelpers';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { financieraLabel } from '@/domains/retail/ventas/lib/ventasFormat';
import { reportPeriodLabel } from '@/shared/patterns/PeriodBand';
import { periodRange, type InventoryRow } from './inventoryList';

export type DetailTab = 'product' | 'price' | 'stock' | 'sales' | 'promotions';

/** Pestañas de la ficha en su orden: la tarjeta PCF solo trae su stock; el servicio no lleva inventario. */
export function visibleTabs(kind: InventoryRow['kind']): DetailTab[] {
    if (kind === 'CARD') return ['stock'];
    if (kind === 'SERVICE') return ['product', 'price', 'sales', 'promotions'];
    return ['product', 'price', 'stock', 'sales', 'promotions'];
}

/** La pestaña elegida si esta ficha la tiene; el servicio cambia stock por ventas; si no, la primera. */
export function activeTabFor(kind: InventoryRow['kind'], tab: DetailTab): DetailTab {
    const tabs = visibleTabs(kind);
    if (tabs.includes(tab)) return tab;
    if (tab === 'stock' && tabs.includes('sales')) return 'sales';
    return tabs[0];
}

export interface StoreInfo {
    code: string;
    name: string;
}

/** Tiendas del retailer por `storeId`. */
export type StoreDirectory = ReadonlyMap<string, StoreInfo>;

/** Nombre y código de una tienda; si no está en el padrón, la cola de su id. */
export function storeInfo(stores: StoreDirectory, storeId: string): StoreInfo {
    const short = `…${storeId.slice(-6)}`;
    return stores.get(storeId) ?? { code: short, name: short };
}

export type AlertDetailKey = 'out' | 'low' | 'slow' | 'top';

export interface AlertDetail {
    key: AlertDetailKey;
    /** Rótulo corto del banner. */
    label: string;
    /** La explicación completa. */
    text: string;
}

/** Cada alerta con rótulo corto y explicación, de la más grave a la menos grave; top rotación no va sin stock. */
export function alertDetails(row: InventoryRow): AlertDetail[] {
    const { outOfStock, lowStock, slowMover, topRotation } = row.alerts;
    const details: AlertDetail[] = [];
    if (outOfStock)
        details.push({ key: 'out', label: 'Sin stock', text: 'Sin stock en la cadena' });
    if (lowStock) {
        const days = typeof row.coverage === 'number' ? Math.round(row.coverage) : 0;
        const daysText = `${String(days)} día${days === 1 ? '' : 's'}`;
        details.push({
            key: 'low',
            label: `Stock bajo · ${daysText}`,
            text: `Stock bajo: cobertura de ${daysText}, menos de 7`,
        });
    }
    if (slowMover) {
        details.push({
            key: 'slow',
            label: 'Lento',
            text: 'Lento: más de 5 piezas sin ventas en 30 días',
        });
    }
    if (topRotation && !outOfStock) {
        details.push({
            key: 'top',
            label: 'Top rotación',
            text: 'Top rotación: sus ventas de 30 días están en el 10% más alto de la cadena',
        });
    }
    return details;
}

export type DistributionTone = 'zero' | 'low' | 'ok';

export function distributionTone(stock: number): DistributionTone {
    if (stock === 0) return 'zero';
    return stock <= 3 ? 'low' : 'ok';
}

/** Monto del SKU en la venta: el neto de sus líneas o del kit que lo trae; sin líneas, lo vendido. */
export function skuAmountCents(sale: BackofficeSale, sku: string): number {
    return skuSoldCents(sale, sku);
}

export type PaymentTone = 'cash' | 'sk' | 'other';

export interface SkuSaleRow {
    saleId: string;
    soldAt: string;
    /** Día de la venta en CDMX, como lo pinta el mock. */
    day: string;
    storeName: string;
    storeId: string;
    zoneName: string | null;
    sellerName: string;
    customerName: string;
    payment: { tone: PaymentTone; label: string };
    amountCents: number;
    /** IMEI o series del SKU en la venta; vacío en SKUs por cantidad. */
    identifiers: string[];
}

const PAYMENT_LABELS = { surekeep: 'Crédito SK', contado: 'Contado' };

function paymentOf(sale: BackofficeSale): SkuSaleRow['payment'] {
    const label = financieraLabel(sale, PAYMENT_LABELS) ?? 'Otra financiera';
    if (sale.type === 'CASH') return { tone: 'cash', label };
    if (sale.type === 'CREDIT' && !sale.externalProvider) return { tone: 'sk', label };
    return { tone: 'other', label };
}

export function toSkuSaleRow(sale: BackofficeSale, sku: string): SkuSaleRow {
    return {
        saleId: sale.saleId,
        soldAt: sale.soldAt,
        day: mxDay(sale.soldAt),
        storeName: sale.storeName ?? '—',
        storeId: sale.storeId ?? '',
        zoneName: sale.zoneName,
        sellerName: sale.sellerName ?? '—',
        customerName: saleCustomerLabel(sale) ?? '—',
        payment: paymentOf(sale),
        amountCents: skuAmountCents(sale, sku),
        identifiers: saleIdentifiers(sale, sku),
    };
}

/** IMEI de la venta y series de los componentes del SKU, sin repetir. */
function saleIdentifiers(sale: BackofficeSale, sku: string): string[] {
    const fromLines = (sale.lines ?? []).flatMap((line) =>
        line.components.filter((c) => c.sku === sku).flatMap((c) => c.identifiers),
    );
    return [...new Set([...(sale.imei ? [sale.imei] : []), ...fromLines])];
}

/** Filtros de `GET /backoffice/sales` para las ventas completadas del SKU en el periodo. */
export function skuSalesFilters(
    retailerId: string,
    sku: string,
    period: InventoryLensPeriod,
    storeId: string | undefined,
    today: string,
): BackofficeSalesFilters {
    const range = periodRange(period, today);
    return {
        retailerId,
        sku,
        status: 'COMPLETED',
        ...(storeId ? { storeId } : {}),
        ...(range ?? {}),
    };
}

/** Nombre de la zona de cada tienda según el padrón de retail-org; sin zona conocida no entra. */
export function storeZones(
    stores: readonly { storeId: string; zoneId: string | null }[],
    zones: readonly { zoneId: string; name: string }[],
): Map<string, string> {
    const names = new Map(zones.map((z) => [z.zoneId, z.name]));
    const result = new Map<string, string>();
    for (const store of stores) {
        const name = store.zoneId ? names.get(store.zoneId) : undefined;
        if (name) result.set(store.storeId, name);
    }
    return result;
}

export type SaleSortColumn = 'date' | 'store' | 'seller' | 'customer' | 'payment' | 'amount';

export interface SaleSortState {
    column: SaleSortColumn;
    direction: 'asc' | 'desc';
}

export const DEFAULT_SALE_SORT: SaleSortState = { column: 'date', direction: 'desc' };

/** Fecha y monto arrancan descendentes, el resto ascendente; la misma columna alterna. */
export function nextSaleSort(current: SaleSortState, column: SaleSortColumn): SaleSortState {
    if (current.column === column) {
        return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { column, direction: column === 'date' || column === 'amount' ? 'desc' : 'asc' };
}

function saleSortValue(row: SkuSaleRow, column: SaleSortColumn): string | number {
    switch (column) {
        case 'date':
            return row.soldAt;
        case 'store':
            return row.storeName.toLowerCase();
        case 'seller':
            return row.sellerName.toLowerCase();
        case 'customer':
            return row.customerName.toLowerCase();
        case 'payment':
            return row.payment.label.toLowerCase();
        case 'amount':
            return row.amountCents;
    }
}

/** Ventas ordenadas por la columna elegida, sin mutar la entrada. */
export function sortSkuSales(rows: readonly SkuSaleRow[], sort: SaleSortState): SkuSaleRow[] {
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const av = saleSortValue(a, sort.column);
        const bv = saleSortValue(b, sort.column);
        if (av < bv) return -direction;
        if (av > bv) return direction;
        return 0;
    });
}

export const MOVEMENT_TYPE_LABEL: Record<InventoryMovementType, string> = {
    ENTRY: 'Entrada',
    RESERVATION: 'Apartado',
    RELEASE: 'Liberación',
    SALE: 'Venta',
    RETURN: 'Devolución',
    ADJUSTMENT: 'Ajuste',
    TRANSFER_OUT: 'Traspaso enviado',
    TRANSFER_IN: 'Traspaso recibido',
    TRANSFER_CANCEL: 'Traspaso cancelado',
};

const MOVEMENT_REASON_LABEL: Record<InventoryMovementReason, string> = {
    ADJUSTMENT: 'Ajuste',
    COUNT_SURPLUS: 'Sobrante de conteo',
    DAMAGED_RETURN: 'Devolución dañada',
    SHRINKAGE: 'Merma',
    WARRANTY: 'Garantía',
};

/** Motivo del movimiento en el libro; los tipos sin motivo llevan guion. */
export function movementReasonLabel(reason: InventoryMovementReason | undefined): string {
    return reason ? MOVEMENT_REASON_LABEL[reason] : '—';
}

/** Nombre de cada usuario retail por su sub de Cognito; solo entran los que tienen nombre. */
export type UserDirectory = ReadonlyMap<string, string>;

export function userDirectory(
    users: readonly { cognitoSub: string; displayName?: string }[],
): UserDirectory {
    const map = new Map<string, string>();
    for (const user of users) if (user.displayName) map.set(user.cognitoSub, user.displayName);
    return map;
}

/** Autor del movimiento: «Sistema», el nombre del padrón o un texto neutro; `null` = padrón cargando. */
export function movementAuthorLabel(createdBy: string, users: UserDirectory | null): string {
    if (createdBy === 'system') return 'Sistema';
    if (users === null) return '—';
    return users.get(createdBy) ?? 'Usuario desconocido';
}

export const PIECE_STATUS_LABEL: Record<InventoryPieceStatus, string> = {
    AVAILABLE: 'Disponible',
    RESERVED: 'Apartada',
    SOLD: 'Vendida',
    RETURNED: 'Devuelta',
    DAMAGED: 'Dañada',
    IN_TRANSIT: 'En tránsito',
    HELD: 'Apartada en venta',
    INACTIVE: 'Dada de baja',
};

const OUTBOUND: ReadonlySet<InventoryMovementType> = new Set(['SALE', 'TRANSFER_OUT']);

/** Apartado y liberación no mueven piezas en piso, como `PIECE_FLOW_MOVEMENT_TYPES` de retail-catalog. */
const NO_FLOOR_EFFECT: ReadonlySet<InventoryMovementType> = new Set(['RESERVATION', 'RELEASE']);

/** Piezas en piso que suma (+) o resta (−) el asiento; `null` si no las mueve. La devolución dañada no suma. */
export function floorDelta(
    movement: Pick<InventoryMovementEntry, 'type' | 'qty' | 'reason'>,
): number | null {
    if (NO_FLOOR_EFFECT.has(movement.type)) return null;
    if (movement.type === 'ADJUSTMENT') return movement.qty;
    if (movement.type === 'RETURN' && movement.reason === 'DAMAGED_RETURN') return 0;
    const magnitude = Math.abs(movement.qty);
    return OUTBOUND.has(movement.type) ? -magnitude : magnitude;
}

/** Cantidad con signo para la tabla: `+3`, `−1`, `0`, o `—` si no mueve piezas. */
export function formatDelta(delta: number | null): string {
    if (delta === null) return '—';
    if (delta === 0) return '0';
    return `${delta < 0 ? '−' : '+'}${String(Math.abs(delta))}`;
}

/** Cantidad con signo del asiento según su efecto en piso. */
export function signedQty(
    movement: Pick<InventoryMovementEntry, 'type' | 'qty' | 'reason'>,
): string {
    return formatDelta(floorDelta(movement));
}

const SKU_ERROR_MESSAGE: Record<string, string> = {
    PRODUCT_NOT_FOUND: 'Este SKU ya no existe en el catálogo de tu cadena.',
    STORE_NOT_IN_RETAILER:
        'La tienda elegida no es de tu cadena. Elige otra o vuelve al consolidado.',
    INVALID_NEXT_TOKEN:
        'La página del libro ya no es válida. Vuelve a cargarlo desde el principio.',
};

/** Qué decirle al admin cuando la ficha o el historial fallan. */
export function skuDetailErrorMessage(error: unknown): string {
    if (error instanceof ApiError && Object.hasOwn(SKU_ERROR_MESSAGE, error.code)) {
        return SKU_ERROR_MESSAGE[error.code];
    }
    return error instanceof Error ? error.message : 'Algo salió mal, intenta de nuevo';
}

/** Origen de un precio del historial. */
export const PRICE_SOURCE_LABEL: Record<string, string> = {
    ZENTRAL_SYNC: 'Zentral',
    BACKOFFICE: 'SureKeep',
};

/** Precio vigente: el registro más reciente del historial con su origen; sin historial, el de la lista. */
export function currentPriceOf(
    history: SkuPriceHistory | undefined,
    lensPriceCents: number | null,
): { cents: number | null; source: string | null } {
    const current = history?.items.at(0);
    if (current === undefined) return { cents: lensPriceCents, source: null };
    return {
        cents: current.basePriceCents,
        source: PRICE_SOURCE_LABEL[current.source] ?? current.source,
    };
}

/** Cobertura del resumen en días; con stock y sin ventas, no se agota. */
export function coverageDaysText(coverage: InventoryLensCoverage, stock: number): string {
    if (stock === 0 || coverage === null) return '—';
    if (coverage === 'INFINITE') return 'Sin ventas, no se agota';
    const days = Math.round(coverage);
    return `${String(days)} día${days === 1 ? '' : 's'}`;
}

/** Nombre del banner: marca y modelo, o la descripción del catálogo, o el SKU; `null` mientras esa descripción carga. */
export function headerName(
    row: Pick<InventoryRow, 'sku' | 'brand' | 'model'>,
    description: string | null | undefined,
    productLoading = false,
): string | null {
    const hasOwnName = row.brand.trim() !== '' || row.model.trim() !== '';
    if (!hasOwnName && productLoading) return null;
    return itemDisplayName({ sku: row.sku, brand: row.brand, model: row.model, description });
}

/** Etiqueta corta de la métrica de ventas del banner y su nombre completo para el `title`. */
export function salesMetricLabel(period: InventoryLensPeriod): { label: string; title: string } {
    return {
        label: period === 'all' ? 'Ventas (todo)' : `Ventas ${period}`,
        title: `Ventas · ${reportPeriodLabel(period).toLowerCase()}`,
    };
}

/** Número de la pestaña Stock y movimientos: solo con traspasos abiertos, con su rótulo accesible. */
export function openTransfersBadge(
    count: number | undefined,
): { count: number; label: string } | null {
    if (count === undefined || count <= 0) return null;
    const plural = count === 1 ? '' : 's';
    return { count, label: `${String(count)} traspaso${plural} abierto${plural}` };
}

/** Renglón de la tabla «Stock por tienda». Lo vendido nunca cuenta como stock. */
export interface StoreDistributionRow {
    storeId: string;
    code: string;
    name: string;
    /** Unidades físicas en la tienda: disponibles más apartadas. */
    onHand: number;
    /** Unidades apartadas en ventas en curso, con o sin pieza fijada. */
    reserved: number;
    /** Lo que se puede vender: `availableToSell` o, sin él, las piezas libres o el disponible. */
    forSale: number;
    /** Ventas del periodo, solo de referencia; `null` sin fuente (tarjetas PCF). */
    sold: number | null;
    pieces: InventoryPieceEntry[];
    /** Piezas marcadas disponibles cuya unidad ya está apartada en una venta sin pieza fijada. */
    reservedWithoutPiece: number;
}

/** Stock por tienda de la fila con sus piezas y apartados, alfabético por nombre de tienda. */
export function storeDistributionRows(
    row: Pick<InventoryRow, 'byStore'>,
    stores: StoreDirectory,
    piecesByStore: readonly InventoryStorePieces[],
    reservedByStore: readonly InventoryStoreReserved[] = [],
): StoreDistributionRow[] {
    const balanceOf = new Map(piecesByStore.map((s) => [s.storeId, s]));
    const reservedOf = new Map(reservedByStore.map((r) => [r.storeId, r.reserved]));
    return row.byStore
        .map(({ storeId, stock, sold, reserved: listReserved }) => {
            const balance = balanceOf.get(storeId);
            const pieces = balance?.pieces ?? [];
            // Disponible y apartadas salen juntos de una sola respuesta: el saldo de la ficha o la lista.
            const fromBalance = balance?.available !== undefined && balance.reserved !== undefined;
            const available = fromBalance ? (balance.available ?? 0) : stock;
            const reserved = fromBalance
                ? (balance.reserved ?? 0)
                : (listReserved ?? reservedOf.get(storeId) ?? 0);
            const freePieces = pieces.filter((p) => p.status === 'AVAILABLE').length;
            const fallback = pieces.length > 0 ? Math.min(freePieces, available) : available;
            const forSale = balance?.availableToSell ?? fallback;
            return {
                storeId,
                ...storeInfo(stores, storeId),
                onHand: available + reserved,
                reserved,
                forSale,
                sold,
                pieces,
                reservedWithoutPiece: pieces.length > 0 ? Math.max(0, freePieces - forSale) : 0,
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

export interface StockFigures {
    onHand: number;
    reserved: number;
    forSale: number;
}

/** Totales de «En tienda», «Apartados» y «Para vender» de las tiendas mostradas. */
export function stockFigures(rows: readonly StoreDistributionRow[]): StockFigures {
    return rows.reduce(
        (sum, r) => ({
            onHand: sum.onHand + r.onHand,
            reserved: sum.reserved + r.reserved,
            forSale: sum.forSale + r.forSale,
        }),
        { onHand: 0, reserved: 0, forSale: 0 },
    );
}

/** «1 apartada», «2 apartadas». */
export function reservedCountLabel(n: number): string {
    return `${String(n)} apartada${n === 1 ? '' : 's'}`;
}

/** Resumen del modal de piezas: «3 en tienda · 1 apartada · 2 para vender». */
export function piecesSummary(
    store: Pick<StoreDistributionRow, 'onHand' | 'reserved' | 'forSale'>,
): string {
    return `${String(store.onHand)} en tienda · ${reservedCountLabel(store.reserved)} · ${String(store.forSale)} para vender`;
}

/** Estado de la pieza en el modal: la AVAILABLE está «En tienda», sin afirmar que es vendible. */
export function pieceStatusLabel(piece: Pick<InventoryPieceEntry, 'status'>): string {
    return piece.status === 'AVAILABLE' ? 'En tienda' : PIECE_STATUS_LABEL[piece.status];
}

/** Aviso del modal cuando hay apartados sin pieza fijada; `null` si no hay. */
export function reservedWithoutPieceNotice(
    store: Pick<StoreDistributionRow, 'reservedWithoutPiece'>,
): string | null {
    const n = store.reservedWithoutPiece;
    if (n <= 0) return null;
    if (n === 1) {
        return 'Una de estas piezas va para una venta en curso; se sabe cuál hasta la entrega.';
    }
    return `${String(n)} de estas piezas van para ventas en curso; se sabe cuáles hasta la entrega.`;
}

/** Pestaña del modal de piezas: las que siguen en la tienda o las vendidas. */
export type PieceSegment = 'store' | 'sold';

/** Estados que cuentan en «En tienda»: los mismos que suman al saldo físico (disponible más apartado). */
const IN_STORE_STATUSES: ReadonlySet<InventoryPieceStatus> = new Set([
    'AVAILABLE',
    'RESERVED',
    'HELD',
]);

/** Si la pieza va en la pestaña; en tránsito, dañada o dada de baja no van en ninguna. */
export function pieceInSegment(
    piece: Pick<InventoryPieceEntry, 'status'>,
    segment: PieceSegment,
): boolean {
    return segment === 'sold' ? piece.status === 'SOLD' : IN_STORE_STATUSES.has(piece.status);
}

/** Piezas de la pestaña que coinciden con el identificador buscado, por identificador. */
export function filterPieces(
    pieces: readonly InventoryPieceEntry[],
    query: string,
    segment: PieceSegment,
): InventoryPieceEntry[] {
    const q = query.trim().toLowerCase();
    return pieces
        .filter(
            (p) =>
                pieceInSegment(p, segment) && (q === '' || p.identifier.toLowerCase().includes(q)),
        )
        .sort((a, b) => a.identifier.localeCompare(b.identifier, 'es'));
}

/** «N de M piezas». */
export function pieceCountLabel(shown: number, total: number): string {
    return `${String(shown)} de ${String(total)} pieza${total === 1 ? '' : 's'}`;
}

/** Tiendas del padrón como opciones, alfabéticas por nombre. */
export function storeOptions(stores: StoreDirectory): { value: string; label: string }[] {
    return [...stores]
        .map(([storeId, { name }]) => ({ value: storeId, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

/** Tarjetas disponibles por tienda según la fila PCF. */
export function cardsByStore(row: Pick<InventoryRow, 'byStore'>): Map<string, number> {
    return new Map(row.byStore.map((s) => [s.storeId, s.stock]));
}

/** Tiendas con tarjetas para dar de baja, alfabéticas, con su disponible en la etiqueta. */
export function writeOffStoreOptions(
    stores: StoreDirectory,
    available: ReadonlyMap<string, number>,
): { value: string; label: string }[] {
    return storeOptions(stores)
        .filter((o) => (available.get(o.value) ?? 0) > 0)
        .map((o) => ({
            ...o,
            label: `${o.label} · ${String(available.get(o.value) ?? 0)} disponibles`,
        }));
}
